/**
 * formatPrice.js — Utilitaires de formatage de prix frontend (§audit-phase1)
 *
 * Consolide les 18+ expressions inline `parseFloat(Web3.utils.fromWei(val, 'mwei')).toFixed(3)`
 * dispersées dans Transactions, Gain, couponDetail, RoyaltiesDueSection, etc.
 *
 * Usage :
 *   import { formatMwei, formatUSDC, parseMwei } from '../../../../utils/formatPrice';
 */

import Web3 from 'web3';

/**
 * formatMwei — Convertit une valeur en mwei (1e-6 USDC) vers une chaîne USDC lisible.
 *
 * @param {string|number|null} val - Valeur en mwei (peut être null/undefined)
 * @param {number} [decimals=2]    - Nombre de décimales (défaut 2 — USDC)
 * @param {string} [fallback='0']  - Valeur retournée si val est falsy
 * @returns {string} ex: "12.50"
 */
export function formatMwei(val, decimals = 2, fallback = '0') {
  if (val == null || val === '' || isNaN(Number(val))) return fallback;
  try {
    return parseFloat(Web3.utils.fromWei(parseFloat(val).toString(), 'mwei')).toFixed(decimals);
  } catch {
    return fallback;
  }
}

/**
 * formatUSDC — Comme formatMwei mais ajoute le suffixe " USDC".
 *
 * @param {string|number|null} val
 * @param {number} [decimals=2]
 * @returns {string} ex: "12.50 USDC"
 */
export function formatUSDC(val, decimals = 2) {
  const n = formatMwei(val, decimals);
  return n === '0' ? '—' : `${n} USDC`;
}

/**
 * formatUSD — Formate un montant USD fiat (déjà en dollars, PAS en mwei) avec le
 * préfixe "$", séparateur de milliers et 2 décimales : "$1,234.56".
 *
 * Remplace les expressions inline
 *   `$${Number(x).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.
 * NE convertit PAS depuis mwei (pour ça → formatMwei).
 *
 * @param {string|number|null} val   - Montant en USD (dollars entiers + décimales)
 * @param {string} [fallback='$0.00'] - Valeur retournée si val n'est pas un nombre fini
 * @returns {string} ex: "$1,234.56"
 */
export function formatUSD(val, fallback = '$0.00') {
  const n = Number(val);
  if (val == null || val === '' || !Number.isFinite(n)) return fallback;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * parseMwei — Convertit une valeur mwei en nombre flottant brut (sans arrondi forcé).
 *
 * @param {string|number|null} val
 * @returns {number}
 */
export function parseMwei(val) {
  if (val == null || val === '') return 0;
  try {
    return parseFloat(Web3.utils.fromWei(parseFloat(val).toString(), 'mwei'));
  } catch {
    return 0;
  }
}

/**
 * formatGwei3 — Convertit gwei → USD (pattern Transactions : fromWei(val,'gwei') * 1000).
 * Utilisé dans la section "Claim Total USD".
 *
 * @param {string|number|null} val
 * @param {number} [decimals=3]
 * @returns {number}
 */
export function formatGwei3(val, decimals = 3) {
  if (val == null || val === '') return 0;
  try {
    return Math.floor((Web3.utils.fromWei(val ? val.toString() : '0', 'gwei') * 1000) * Math.pow(10, decimals)) / Math.pow(10, decimals);
  } catch {
    return 0;
  }
}
